import { useState } from "react";
import { useDispatch } from "react-redux";
import { range } from "lodash";

import Box from "@mui/material/Box";
import ButtonGroup from "@mui/material/ButtonGroup";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import IconButton from "@mui/material/IconButton";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import PentagonIcon from "@mui/icons-material/Pentagon";
import PanToolAltIcon from "@mui/icons-material/PanToolAlt";
import RectangleOutlinedIcon from "@mui/icons-material/RectangleOutlined";
import PolylineIcon from "@mui/icons-material/Polyline";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

import {
  DrawPolygonMode,
  DrawRectangleMode,
  ModifyMode,
  ViewMode,
} from "@deck.gl-community/editable-layers";

import StyledTooltip from "../UI/StyledTooltip";

import { BUTTON_DIMENSION } from "../../config/config.polygonTool";
import { iconButtonStyle, styles } from "../../config/config.theme";
import { homePosition } from "../../config/config.layout";
import { alertActions, polygonActions } from "../../store";

const PolygonTool = ({
  features,
  onChange,
  polygonLayerVisibility,
  selectedFeatureIndexes,
  setMode,
  setPolygonLayerVisibility,
  toolActive,
}) => {
  const dispatch = useDispatch();
  // mode as string for use as buttonGroup value
  const [modeString, setModeString] = useState("select");

  const buttonStyle = {
    ...iconButtonStyle,
    fontSize: "2rem",
    m: 0,
    width: BUTTON_DIMENSION,
    height: BUTTON_DIMENSION,
    "&:hover": { backgroundColor: `${styles["background-secondary"]}` },
    "&.Mui-selected": {
      backgroundColor: styles["background-quaternary"],
      "&:hover": { backgroundColor: styles["background-quaternary"] },
    },
  };

  const handleChange = (e, newMode) => {
    switch (newMode) {
      case "select":
        setModeString(newMode);
        setMode(() => ViewMode);
        break;
      case "modify":
        setMode(() => ModifyMode);
        if (features.features.length === 0) {
          dispatch(
            alertActions.addAlert({
              alertId: "edit warning",
              severity: "info",
              message: "No polygons to edit",
            })
          );
        } else {
          dispatch(
            polygonActions.setSelectedFeatureIndexes(
              range(features.features.length)
            )
          );
        }

        setModeString(newMode);
        break;
      case "drawPolygon":
        setModeString(newMode);
        setMode(() => DrawPolygonMode);
        break;
      case "drawRectangle":
        setModeString(newMode);
        setMode(() => DrawRectangleMode);
        break;
      default:
        return;
    }
  };

  const onDelete = () => {
    dispatch(polygonActions.deleteSelectedFeatures());
  };

  const ButtonContent = ({ icon, iconLabel }) => {
    return (
      <Box
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        {icon}
        <Typography
          variant="p"
          sx={{
            fontSize: "0.7rem",
            color: styles["text-primary"],
            textTransform: "none",
          }}
        >
          {iconLabel}
        </Typography>
      </Box>
    );
  };

  const toggleButtonProps = {
    variant: "outlined",
    sx: buttonStyle,
  };
  const buttons = [
    <ToggleButton
      key="select"
      aria-label="select"
      value="select"
      {...toggleButtonProps}
    >
      <ButtonContent
        icon={<PanToolAltIcon color="primary" />}
        iconLabel="Select"
      />
    </ToggleButton>,
    <ToggleButton
      key="draw polygon"
      aria-label="draw polygon"
      value="drawPolygon"
      {...toggleButtonProps}
    >
      <ButtonContent
        icon={<PolylineIcon color="primary" />}
        iconLabel="Polygon"
      />
    </ToggleButton>,
    <ToggleButton
      key="draw rectangle"
      aria-label="draw rectangle"
      value="drawRectangle"
      {...toggleButtonProps}
    >
      <ButtonContent
        icon={<RectangleOutlinedIcon color="primary" />}
        iconLabel="Rectangle"
      />
    </ToggleButton>,
  ];

  buttons.push(
    <ToggleButton
      key="modify"
      aria-label="modify"
      value="modify"
      variant="outlined"
      sx={buttonStyle}
    >
      <ButtonContent
        icon={<ModeEditOutlineOutlinedIcon color={"primary"} />}
        iconLabel="Edit"
      />
    </ToggleButton>
  );

  return (
    <>
      <StyledTooltip
        title="Polygon tool"
        placement={
          homePosition.tooltipPlacement
            ? homePosition.tooltipPlacement
            : "right"
        }
        disableFocusListener
        enterTouchDelay={0}
      >
        <ToggleButton
          aria-label="draw a polygon"
          value="check"
          selected={toolActive}
          onChange={onChange}
          // color="primary"
          sx={{
            ...iconButtonStyle,
            // backgroundColor: selected ? "#fff" :  styles["background-primary"],
            fontSize: "24px",
            "&.Mui-selected": {
              backgroundColor: styles["background-quaternary"],
              "&:hover": { backgroundColor: styles["background-quaternary"] },
            },
            "&:hover": { backgroundColor: styles["background-primary"] },
          }}
        >
          <PentagonIcon fontSize="1.5rem" color="primary" />
        </ToggleButton>
      </StyledTooltip>

      {toolActive && (
        <ButtonGroup
          orientation="vertical"
          sx={{
            position: "absolute",
            right: "52px",
            top: "155px",
          }}
        >
          <ToggleButtonGroup
            exclusive={true}
            onChange={handleChange}
            orientation="vertical"
            aria-label="vertical contained button group"
            variant="contained"
            value={modeString}
          >
            {buttons}
          </ToggleButtonGroup>
          {features.features.length > 0 && (
            <IconButton
              key="show hide"
              aria-label="show hide polygon layer"
              variant="outlined"
              onClick={() =>
                setPolygonLayerVisibility((prevState) => !prevState)
              }
              sx={{
                ...buttonStyle,
                height: BUTTON_DIMENSION,
                width: BUTTON_DIMENSION,
              }}
            >
              {polygonLayerVisibility && (
                <ButtonContent
                  icon={<VisibilityOutlinedIcon color="primary" />}
                  iconLabel="Visible"
                />
              )}
              {!polygonLayerVisibility && (
                <ButtonContent
                  icon={<VisibilityOffOutlinedIcon color="primary" />}
                  iconLabel="Hidden"
                />
              )}
            </IconButton>
          )}
          {selectedFeatureIndexes.length > 0 && (
            <IconButton
              key="delete"
              aria-label="delete selected polygon features"
              variant="outlined"
              onClick={onDelete}
              sx={{
                ...buttonStyle,
                height: BUTTON_DIMENSION,
                width: BUTTON_DIMENSION,
              }}
            >
              <ButtonContent
                icon={<DeleteOutlinedIcon color="primary" />}
                iconLabel="Delete selected"
              />
            </IconButton>
          )}
        </ButtonGroup>
      )}
    </>
  );
};

export default PolygonTool;
